import { useContent } from "@/context/ContentContext";

/**
 * Renders a CMS-editable legal page (Privacy Policy or Terms of Service).
 * Content is stored on window.content.<sectionKey>.{title, updated_at, body}.
 * The body supports **bold** markdown-lite (emphasis on section headings) and blank-line paragraph breaks.
 */
function renderBody(body) {
  const paragraphs = String(body || "").split(/\n\s*\n/);
  return paragraphs.map((p, i) => {
    // Bold markdown-lite: replace **text** → <strong>text</strong>
    const parts = p.split(/(\*\*[^*]+\*\*)/g).map((seg, j) => {
      if (seg.startsWith("**") && seg.endsWith("**")) {
        return <strong key={j} className="text-tmf-navy">{seg.slice(2, -2)}</strong>;
      }
      // Preserve single line breaks inside a paragraph
      return seg.split("\n").flatMap((ln, k, arr) => (k < arr.length - 1 ? [ln, <br key={`br-${j}-${k}`} />] : [ln]));
    });
    return <p key={i} className="mb-4 text-slate-700 leading-relaxed">{parts}</p>;
  });
}

export default function LegalPage({ sectionKey, fallbackTitle }) {
  const { content } = useContent();
  const section = content?.[sectionKey] || {};
  const title = section.title || fallbackTitle;
  const updatedAt = section.updated_at;
  const body = section.body || "This content is being prepared. Please check back soon or email sales@tmfsilversure.com for details.";

  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <div className="border-b border-slate-200 pb-6 mb-8">
        <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">Legal</div>
        <h1 data-testid="legal-title" className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight text-tmf-navy">{title}</h1>
        {updatedAt && (
          <div className="mt-3 text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">Last updated · {updatedAt}</div>
        )}
      </div>
      <div data-testid="legal-body" className="prose max-w-none">
        {renderBody(body)}
      </div>
    </div>
  );
}
